import { useRef, useState } from "react";
import { PenLine, Loader2, BadgeCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import { SignaturePad } from "@/components/webadvisor/SignaturePad";

export const SignedBadge = ({ token, signature, company }) => (
  <div className="flex flex-wrap items-center gap-4 bg-green-50 border border-green-200 rounded-lg p-3" data-testid={company ? "countersigned-badge" : "signed-badge"}>
    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/webadvisor/public/proposal/${token}/${company ? "countersignature" : "signature"}.png`} alt="Signature" className="h-12 w-[180px] object-contain bg-white rounded border border-green-100 px-2" data-testid={company ? "countersigned-image" : "signed-image"} />
    <div className="text-sm text-green-800">
      <p className="font-bold flex items-center gap-1.5"><BadgeCheck className="w-4 h-4" />{company ? "Countersigned for ShopAdvisor™ by" : "Signed by"} {signature.name}{company && signature.title ? `, ${signature.title}` : ""}</p>
      <p className="text-xs">{new Date(signature.signed_at).toLocaleString()}</p>
    </div>
  </div>
);

export const SignAgreement = ({ token, defaultName = "", onSigned }) => {
  const pad = useRef(null);
  const [name, setName] = useState(defaultName);
  const [drawn, setDrawn] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const sign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/webadvisor/public/proposal/${token}/sign`, { name, signature: pad.current.toDataURL(), agree });
      toast.success("Agreement signed"); onSigned(data.signature);
    } catch (err) { const d = err.response?.data?.detail; toast.error(Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || "Couldn't save your signature"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="sign-agreement">
      <p className="font-bold text-slate-900 flex items-center gap-2"><PenLine className="w-4 h-4 text-blue-600" />Sign the agreement</p>
      <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" className="bg-white" data-testid="sign-name" />
      <SignaturePad ref={pad} onChange={setDrawn} />
      <label className="flex items-start gap-2 text-sm text-slate-600">
        <Checkbox checked={agree} onCheckedChange={v => setAgree(!!v)} className="mt-0.5" data-testid="sign-agree" />
        I agree to the plan, terms and invoice, and that my electronic signature is legally binding. Today's date is added automatically.
      </label>
      <button onClick={sign} disabled={busy || name.trim().length < 2 || !drawn || !agree} className="bg-slate-900 hover:bg-slate-700 text-white font-bold text-sm px-5 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="sign-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}Sign agreement
      </button>
    </div>
  );
};
